import type * as t from 'io-ts';

import {
  ScopeName,
  ScopeType,
  TRANSCEND_SCOPES,
} from '@transcend-io/privacy-types';
import { getEntries } from '@transcend-io/type-utils';

import type { RestEndpoint } from '@main/schema-utils';

import * as endpoints from './endpoints';

/**
 * Preference management scopes excluding the "smart" scopes that grant full access or Full view access.
 */
type PreferenceManagementScopesMinusSmart =
  | ScopeName.ViewManagedConsentDatabaseAdminApi
  | ScopeName.ManageStoredPreferences;

/**
 * "Smart" scopes that grant full access or Full view access.
 */
type SmartScopes = ScopeName.FullAdmin | ScopeName.ReadOnly;

/**
 * All preference management scopes.
 */
type PreferenceManagementScopes =
  | PreferenceManagementScopesMinusSmart
  | SmartScopes;

/**
 *
 * Get a unique key for a rest endpoint based on its route and method.
 *
 * Functionality:
 * 1. Combines the route and method of the endpoint to create a unique string.
 *
 * @param endpoint - The endpoint to get the key for
 * @returns The unique key for the endpoint
 */
function retrieveEndpointKey<
  TBody extends t.Any,
  TResponseBody extends t.Any,
  THeaders extends t.Any,
>(endpoint: RestEndpoint<TBody, TResponseBody, THeaders>): string {
  return `${endpoint.route}${endpoint.method}`;
}

/**
 * Preference management routes that do not require any authorization checks.
 *
 * These are routes:
 * 1. Where controllers handle auth internally (e.g. via user tokens or other means).
 * 2. Publicly accessible routes that do not require authentication.
 */
export const PUBLIC_PM_ROUTES = [
  retrieveEndpointKey(endpoints.getConsentPreferences),
  retrieveEndpointKey(endpoints.updateConsentPreferences),
];

/**
 * Preference management routes that are accessible by backend services.
 * These routes are intended for internal service-to-service communication and
 * may have different authorization requirements compared to routes exposed to end-users.
 */
export const BACKEND_ACCESSIBLE_ROUTES = [
  retrieveEndpointKey(endpoints.deleteCacheValues),
];

/**
 * A mapping of preference management scopes to sets of routes,
 * where any one of the listed scopes grants access to the route.
 *
 * This mapping excludes the "smart" scopes that grant full access or Full view access.
 */
const PM_SCOPE_TO_ROUTES_MINUS_SMART: {
  [name in PreferenceManagementScopesMinusSmart]: string[];
} = {
  [ScopeName.ViewManagedConsentDatabaseAdminApi]: [
    retrieveEndpointKey(endpoints.queryConsentPreferences),
  ],
  [ScopeName.ManageStoredPreferences]: [],
};

export const PM_SCOPE_TO_ROUTES: {
  [name in PreferenceManagementScopes]: string[];
} = {
  ...PM_SCOPE_TO_ROUTES_MINUS_SMART,
  [ScopeName.FullAdmin]: Object.values(PM_SCOPE_TO_ROUTES_MINUS_SMART).flat(),
  [ScopeName.ReadOnly]: Array.from(
    new Set(
      getEntries(PM_SCOPE_TO_ROUTES_MINUS_SMART)
        .filter(
          ([scopeName]) => TRANSCEND_SCOPES[scopeName].type === ScopeType.View,
        )
        .flatMap(([, values]) => values),
    ),
  ),
};

/**
 * All preference management routes, including those that do not require scopes.
 */
export const ALL_PM_ROUTES = Array.from(
  new Set([
    ...Object.values(PM_SCOPE_TO_ROUTES).flat(),
    ...PUBLIC_PM_ROUTES,
    ...BACKEND_ACCESSIBLE_ROUTES,
  ]),
);
